import React, { useState, useEffect } from "react"
import { Link, useParams } from "react-router-dom"
import {
  Table,
  Col,
  Row,
  UncontrolledAlert,
  Spinner,
} from "reactstrap"
import api from "API/Api"
import {
  AvForm,
  AvField,
  AvRadioGroup,
  AvRadio,
} from "availity-reactstrap-validation"
import RatechartFile from "./../../../../assets/Ratechart.csv"
import PageLoader from "./../../../../assets/loading/pageLoad1.gif"

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB

const EditRatechart = () => {
  const { id } = useParams()
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [pageLoad, setPageLoad] = useState(true)
  const [fatAndSnf, setFatAndSnf] = useState("")
  const [round, setRound] = useState(false)
  const [limit, setLimit] = useState(false)
  const [ts, setTs] = useState([{ tsUpTo: "", tsRate: "" }])
  const [minFat, setMinFat] = useState("")
  const [maxFat, setMaxFat] = useState("")
  const [minSnf, setMinSnf] = useState("")
  const [maxSnf, setMaxSnf] = useState("")
  const [fats, setFats] = useState([])
  const [snfs, setSnfs] = useState([])
  const [showTbl, setShowTbl] = useState(false)
  const [chart, setChart] = useState({})
  const [fatSnf, setFatSnf] = useState({})
  const [entries, setEntries] = useState(1)
  const [priceData, setPriceData] = useState([])
  const [importError, setImportError] = useState("")
  const [fatIncrement, setFatIncrement] = useState("0.1")
  const [snfIncrement, setSnfIncrement] = useState("0.1")

  useEffect(() => {
    getData()
  }, [])

  const getData = async () => {
    try {
      const chartResp = await api.get("rate-chart/" + id)
      if (chartResp.data.success && chartResp.data.data && chartResp.data.data.length > 0) {
        const chartData = chartResp.data.data[0]
        setChart(chartData)

        // Parse fat_snf JSON safely
        let parsedFatSnf = {}
        try {
          if (chartData.fat_snf) {
            parsedFatSnf = JSON.parse(chartData.fat_snf)
          }
        } catch (e) {
          console.error("Failed to parse fat_snf JSON:", e)
        }
        setFatSnf(parsedFatSnf)

        setFatAndSnf(chartData.calculationType)
        const mf = (chartData.fatMin || '').trim()
        const xf = (chartData.fatMax || '').trim()
        const ms = (chartData.snfMin || '').trim()
        const xs = (chartData.snfMax || '').trim()
        setMinFat(mf)
        setMaxFat(xf)
        setMinSnf(ms)
        setMaxSnf(xs)
        setRound(chartData.roundOff1 == 1)

        // Load saved increments
        const fi = chartData.fatIncrement || '0.1'
        const si = chartData.snfIncrement || '0.1'
        setFatIncrement(fi)
        setSnfIncrement(si)

        // Auto-generate grid if we have valid ranges
        if (mf && xf && ms && xs) {
          generateGrid(mf, xf, ms, xs, fi, si)
        }
      }
    } catch (error) {
      console.error("Failed to fetch rate chart:", error)
    }
    setPageLoad(false)
  }

  const handleValidSubmit = async (event, values) => {
    setLoading(true)
    try {
      if (!values.upperRoundOff) {
        values.upperRoundOff = chart.upperRoundOff || '0'
      }
      values.fat_snf = JSON.stringify(values.fat_snf)
      const resp = await api.put("update-rate-chart/" + id, values)
      if (resp.data.success === true) setSubmitted(true)
    } catch (error) {
      // Handled by global interceptor
    }
    setLoading(false)
  }

  const calcChangeHandler = e => {
    const v = e.target.value
    if (v == 1) setFatAndSnf(1)
    else if (v == 2 || v == 3) setFatAndSnf(2)
    else if (v == 4) setFatAndSnf(4)
    else if (v == 5) setFatAndSnf(5)
    else if (v == 6) setFatAndSnf(6)
    else setFatAndSnf(false)
  }

  const roundChangeHandler = e => {
    e.target.value == 1 ? setRound(true) : setRound(false)
  }
  const limitChangeHandler = e => {
    e.target.value == 1 ? setLimit(true) : setLimit(false)
  }
  const addRow = e => {
    e.preventDefault()
    setTs([...ts, { tsUpTo: "", tsRate: "" }])
  }
  const deleteRow = (e, i) => {
    const data = [...ts]
    data.splice(i, 1)
    setTs(data)
  }

  const generateGrid = (mf, xf, ms, xs, fatInc, snfInc) => {
    const minFVal = parseFloat(mf)
    const maxFVal = parseFloat(xf)
    const minSVal = parseFloat(ms)
    const maxSVal = parseFloat(xs)
    const fatStep = parseFloat(fatInc || fatIncrement) || 0.1
    const snfStep = parseFloat(snfInc || snfIncrement) || 0.1

    if (isNaN(minFVal) || isNaN(maxFVal) || isNaN(minSVal) || isNaN(maxSVal)) return
    if (minFVal >= maxFVal || minSVal >= maxSVal) return
    if (fatStep <= 0 || snfStep <= 0) return

    const fatCount = Math.floor((maxFVal - minFVal) / fatStep) + 1
    const snfCount = Math.floor((maxSVal - minSVal) / snfStep) + 1
    if (fatCount > 200 || snfCount > 200) return

    setShowTbl(true)
    // Always use 2 decimal places — dairy standard for FAT/SNF and matches DB key format
    let fat = []
    for (let i = minFVal; i <= maxFVal + 0.001; i += fatStep) {
      fat.push(i.toFixed(2))
    }
    let snf = []
    for (let i = minSVal; i <= maxSVal + 0.001; i += snfStep) {
      snf.push(i.toFixed(2))
    }
    setFats(fat)
    setSnfs(snf)
  }

  const calculatePrice = () => {
    generateGrid(minFat, maxFat, minSnf, maxSnf, fatIncrement, snfIncrement)
  }

  function fileChangeHandler(e) {
    setImportError("")
    const file = e.target.files[0]
    if (!file) return

    if (file.size > MAX_FILE_SIZE) {
      setImportError("File too large. Maximum 5MB.")
      e.target.value = ""
      return
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setImportError("Only .csv files are supported.")
      e.target.value = ""
      return
    }

    const reader = new FileReader()
    reader.onerror = () => setImportError("Failed to read file.")
    reader.onload = function (evt) {
      try {
        const rawText = evt.target.result.replace(/\r\n/g, "\n").replace(/\r/g, "\n")
        const lines = rawText.split("\n").filter(line => line.trim() !== "")

        if (lines.length < 2) {
          setImportError("CSV must have header row and at least one data row.")
          return
        }

        const headers = lines[0].split(",").map(h => h.trim())
        if (headers[0] !== "FAT/SNF") {
          setImportError('First column header must be "FAT/SNF".')
          return
        }

        const result = []
        const rel_data = {}
        for (let i = 1; i < lines.length; i++) {
          const currentline = lines[i].split(",").map(v => v.trim())
          const obj = {}
          for (let j = 0; j < headers.length; j++) {
            obj[headers[j]] = currentline[j] || ""
          }
          result.push(obj)
          const fatVal = obj["FAT/SNF"]
          if (fatVal) rel_data[fatVal] = obj
        }

        if (result.length === 0) {
          setImportError("No valid rows found.")
          return
        }

        const fat_keys = Object.keys(rel_data)
        const snf_keys = Object.keys(rel_data[fat_keys[0]]).filter(k => k !== "FAT/SNF")

        setFatSnf(rel_data)
        setPriceData(result)
        setMinFat(fat_keys[0])
        setMaxFat(fat_keys[fat_keys.length - 1])
        setMinSnf(snf_keys[0])
        setMaxSnf(snf_keys[snf_keys.length - 1])
        generateGrid(fat_keys[0], fat_keys[fat_keys.length - 1], snf_keys[0], snf_keys[snf_keys.length - 1])
        setImportError("")
      } catch (err) {
        setImportError("Failed to parse CSV: " + err.message)
      }
    }
    reader.readAsText(file)
  }

  // Export current grid to CSV
  const exportToCsv = () => {
    if (fats.length === 0 || snfs.length === 0) return
    let csv = "FAT/SNF," + snfs.join(",") + "\n"
    fats.forEach(fat => {
      const row = [fat]
      snfs.forEach(snf => {
        const val = fatSnf[fat] ? (fatSnf[fat][snf] || "") : ""
        row.push(val)
      })
      csv += row.join(",") + "\n"
    })
    const blob = new Blob([csv], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = (chart.ratechartName || "ratechart") + ".csv"
    a.click()
    URL.revokeObjectURL(url)
  }

  if (pageLoad) {
    return <div className="pageLoader"><img src={PageLoader} alt="Loading..." /></div>
  }

  return (
    <>
      <div className="dashBody">
        <div className="font-size-18 mb-0 dashHeader">
          <Row>
            <Col md={6}>
              <h4>Edit Rate Chart — {chart.ratechartName || ''}</h4>
            </Col>
            <Col md={6} style={{ textAlign: "right" }}>
              <Link to="/rate-charts" className="dashBtn">
                <i className=" fas fa-chevron-left "></i> <b> Back</b>
              </Link>
            </Col>
          </Row>
        </div>
        <div className="dashContent">
          <AvForm
            style={{ position: "relative" }}
            className="form-horizontal"
            onValidSubmit={(e, v) => {
              handleValidSubmit(e, v)
            }}
          >
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <AvField
                    type="select" name="milkType" label="Milk Type"
                    required value={chart.milkType}
                  >
                    <option value="">Select Milk Type</option>
                    <option value="1">Cow Milk</option>
                    <option value="2">Buffalo Milk</option>
                    <option value="3">Common Milk</option>
                  </AvField>
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <AvField
                    name="ratechartName" label="Rate Chart Name"
                    value={chart.ratechartName}
                    className="form-control" type="text" required
                  />
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <AvField
                    type="select" name="calculationType" label="Calculation Type"
                    required onChange={e => calcChangeHandler(e)} value={chart.calculationType}
                  >
                    <option value="">Select Calculation Type</option>
                    <option value="1">FAT &amp; SNF</option>
                    <option value="2">TS Based (FAT &amp; SNF)</option>
                    <option value="3">TS Based (SNF Only)</option>
                    <option value="4">FAT Only</option>
                    <option value="5">KG FAT Rate</option>
                    <option value="6">Flat Rate</option>
                  </AvField>
                </div>
              </Col>

              {/* Round off (FAT & SNF) */}
              {fatAndSnf == 1 ? (
                <>
                  <Col md={6}>
                    <div className="mb-3">
                      <AvRadioGroup
                        inline name="roundOff1"
                        label="Round off to 1 decimal?"
                        required errorMessage="Pick one!"
                        onChange={e => roundChangeHandler(e)}
                        value={chart.roundOff1}
                      >
                        <AvRadio label="Yes" value="1" />
                        <AvRadio label="No" value="0" />
                      </AvRadioGroup>
                    </div>
                  </Col>
                  {round ? (
                    <Col md={6}>
                      <div className="mb-3">
                        <AvRadioGroup
                          inline name="upperRoundOff"
                          label="Upper round off if 2nd decimal ≥"
                          required errorMessage="Pick one!"
                          value={chart.upperRoundOff}
                        >
                          <AvRadio label="5" value="5" />
                          <AvRadio label="6" value="6" />
                          <AvRadio label="7" value="7" />
                          <AvRadio label="8" value="8" />
                          <AvRadio label="9" value="9" />
                        </AvRadioGroup>
                      </div>
                    </Col>
                  ) : (
                    <AvField name="upperRoundOff" value="0" type="hidden" />
                  )}
                </>
              ) : null}

              {/* Entry mode */}
              <Col md={6}>
                <div className="mb-3">
                  <AvRadioGroup
                    inline name="entries" label="Entries"
                    required errorMessage="Pick one!"
                    onChange={e => setEntries(parseInt(e.target.value))}
                    value="1"
                  >
                    <AvRadio label="Manual" value="1" />
                    <AvRadio label="CSV Upload" value="2" />
                  </AvRadioGroup>
                </div>
              </Col>

              {/* CSV Upload */}
              {entries == 2 ? (
                <Col md={6}>
                  <div className="mb-3">
                    <AvField
                      name="file" label="Upload (CSV Only)"
                      accept=".csv" value=""
                      onChange={e => fileChangeHandler(e)}
                      className="form-control" type="file"
                    />
                    <AvField name="fat_snf" value={priceData} className="form-control" type="hidden" />
                    <p style={{fontSize:'13px', marginTop:'4px'}}>
                      Sample CSV file <Link to={RatechartFile} target="_blank" download style={{fontWeight:'600'}}>⬇ Download</Link>
                    </p>
                  </div>
                </Col>
              ) : null}

              {/* Import Error */}
              {importError ? (
                <Col md={12}>
                  <UncontrolledAlert color="danger">
                    <strong>⚠ </strong>{importError}
                  </UncontrolledAlert>
                </Col>
              ) : null}

              {/* Manual Grid */}
              {entries == 1 ? (
                <>
                  {fatAndSnf == 1 || fatAndSnf == 4 ? (
                    <Col md={12}>
                      <div className="mb-3">
                        <Row>
                          <Col md={1}><label>FAT Range</label></Col>
                          <Col md={3}>
                            <AvField name="fatMin" value={chart.fatMin} onChange={e => setMinFat(e.target.value)} className="form-control" type="text" required />
                          </Col>
                          <Col md={3}>
                            <AvField name="fatMax" value={chart.fatMax} onChange={e => setMaxFat(e.target.value)} className="form-control" type="text" required />
                          </Col>
                          <Col md={1}><label>Increment</label></Col>
                          <Col md={4}>
                            <AvField
                              name="fatRange" className="form-control" type="text"
                              value={fatIncrement}
                              onChange={e => setFatIncrement(e.target.value)}
                              placeholder="e.g. 0.1, 0.2, 0.5"
                            />
                          </Col>
                        </Row>
                      </div>
                    </Col>
                  ) : null}
                  {fatAndSnf == 1 ? (
                    <>
                      <Col md={12}>
                        <div className="mb-3">
                          <Row>
                            <Col md={1}><label>SNF Range</label></Col>
                            <Col md={3}>
                              <AvField name="snfMin" value={chart.snfMin} className="form-control" onChange={e => setMinSnf(e.target.value)} type="text" required />
                            </Col>
                            <Col md={3}>
                              <AvField name="snfMax" value={chart.snfMax} onChange={e => setMaxSnf(e.target.value)} className="form-control" type="text" required />
                            </Col>
                            <Col md={1}><label>Increment</label></Col>
                            <Col md={4}>
                              <AvField
                                name="snfRange" className="form-control" type="text"
                                value={snfIncrement}
                                onChange={e => setSnfIncrement(e.target.value)}
                                placeholder="e.g. 0.1, 0.2, 0.5"
                              />
                            </Col>
                          </Row>
                        </div>
                        <button className="btn btn-primary me-2" type="button" onClick={calculatePrice}>
                          Generate Grid
                        </button>
                        {showTbl && (
                          <button className="btn btn-outline-success" type="button" onClick={exportToCsv}>
                            ⬇ Export to CSV
                          </button>
                        )}
                      </Col>
                      {showTbl ? (
                        <div className="table-responsive mt-3">
                        <Table>
                          <thead>
                            <tr>
                              <th style={{position:'sticky', left:0, background:'#fff', zIndex:1}}>FAT/SNF</th>
                              {snfs.map(snf => (
                                <th key={snf}>{snf}</th>
                              ))}
                            </tr>
                            {fats.map(fat => (
                              <tr key={fat}>
                                <th style={{position:'sticky', left:0, background:'#f8f9fa', zIndex:1}}>{fat}</th>
                                {snfs.map((snf, i) => (
                                  <th key={i}>
                                    <AvField
                                      style={{padding:'2px 4px', minWidth:'60px', textAlign:'center'}}
                                      value={fatSnf[fat] ? (fatSnf[fat][snf] || "") : ""}
                                      type="text"
                                      name={"fat_snf[" + fat + "][" + snf + "]"}
                                    />
                                  </th>
                                ))}
                              </tr>
                            ))}
                          </thead>
                        </Table>
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </>
              ) : null}

              {/* TS Based Options */}
              {fatAndSnf == 2 || fatAndSnf == 5 ? (
                <>
                  <Col md={6}>
                    <div className="mb-3">
                      <AvRadioGroup inline name="fatSnfLimit" label="Limit Min/Max FAT, SNF?" required errorMessage="Pick one!" onChange={e => limitChangeHandler(e)}>
                        <AvRadio label="Yes" value="1" />
                        <AvRadio label="No" value="0" />
                      </AvRadioGroup>
                    </div>
                  </Col>
                  {limit ? (
                    <>
                      <Col md={6}><div className="mb-3"><AvField name="minFat" label="Min FAT" value="" className="form-control" type="text" required /></div></Col>
                      <Col md={6}><div className="mb-3"><AvField name="maxFat" label="Max FAT" value="" className="form-control" type="text" required /></div></Col>
                      <Col md={6}><div className="mb-3"><AvField name="minSnf" label="Min SNF" value="" className="form-control" type="text" required /></div></Col>
                      <Col md={6}><div className="mb-3"><AvField name="maxSnf" label="Max SNF" value="" className="form-control" type="text" required /></div></Col>
                    </>
                  ) : null}
                </>
              ) : null}

              {fatAndSnf == 2 ? (
                <>
                  <Col md={6}>
                    <div className="mb-3">
                      <AvRadioGroup inline name="rateDecimalPrecision" label="Rate Decimal Precision" required errorMessage="Pick one!">
                        <AvRadio label="1" value="1" />
                        <AvRadio label="2" value="2" />
                      </AvRadioGroup>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <AvRadioGroup inline name="excessDecimal" label="Excess Decimal Operation" required errorMessage="Pick one!">
                        <AvRadio label="Round Off" value="1" />
                        <AvRadio label="Truncate" value="2" />
                      </AvRadioGroup>
                    </div>
                  </Col>
                  <h5 className="mt-3 mb-3">TS Items Information</h5>
                  <Table className="table mb-0">
                    <thead>
                      <tr>
                        <th>S.No</th><th>TS Greater Than</th><th>TS Upto</th><th>TS Rate</th>
                        <th><button type="button" className="btn btn-primary btn-sm" onClick={addRow}><i className="fa fa-plus"></i></button></th>
                      </tr>
                    </thead>
                    <tbody>
                      {ts.map((t, i) => (
                        <tr key={i}>
                          <td>{i + 1}</td><td></td>
                          <td><AvField name={`tsUpTo_${i}`} value="" className="form-control" type="text" required /></td>
                          <td><AvField name={`tsRate_${i}`} value="" className="form-control" type="text" required /></td>
                          <td>{ts.length > 1 && <i className="fas fa-trash-alt icons icon-delete" onClick={e => deleteRow(e, i)} style={{cursor:'pointer'}}></i>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              ) : null}

              {fatAndSnf == 5 ? (
                <Col md={6}><div className="mb-3"><AvField name="fatRate" label="KG FAT Rate (₹)" value="" className="form-control" type="text" required /></div></Col>
              ) : null}

              {fatAndSnf == 6 ? (
                <Col md={6}><div className="mb-3"><AvField name="flatRate" label="Flat Rate per Litre (₹)" value="" className="form-control" type="text" required /></div></Col>
              ) : null}
            </Row>

            <div className="mb-3 row mt-4">
              <div className="col-6">
                {submitted ? (
                  <UncontrolledAlert color="success">
                    <strong>Rate Chart Updated Successfully!</strong>
                  </UncontrolledAlert>
                ) : null}
              </div>
              <div className="col-6 text-end">
                <button
                  className="btn btn-primary w-md waves-effect waves-light"
                  type="submit" disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" />
                      <span style={{ paddingLeft: "10px" }}>Updating...</span>
                    </>
                  ) : (
                    <span>Update Rate Chart</span>
                  )}
                </button>
              </div>
            </div>
          </AvForm>
        </div>
      </div>
    </>
  )
}

export default EditRatechart
